import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client.js";
import { filingHref } from "../lib/citations.js";

/**
 * The owner's own thinking, on the situation page.
 *
 * Nothing automatic modifies, summarises or reorganises it: no autosave, no formatting on
 * blur, no placeholder that becomes content. The textarea holds exactly what was typed
 * until an explicit Save, and Save sends it byte-for-byte.
 *
 * Open questions come first: they are the one place the tool records what it does not
 * know, and a long thesis above them would bury them.
 */
export function Workspace({ situationId }: { situationId: string }) {
  return (
    <>
      <div className="sechd">Open questions</div>
      <Questions situationId={situationId} />

      <div className="sechd">Thesis</div>
      <Thesis situationId={situationId} />

      <Excerpts situationId={situationId} />
    </>
  );
}

/**
 * A situation's open questions: add one, resolve it with an answer, reopen it, and
 * optionally list resolved ones too.
 *
 * @internal
 */
function Questions({ situationId }: { situationId: string }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [showResolved, setShowResolved] = useState(false);

  const questions = useQuery({
    queryKey: ["questions", situationId, showResolved],
    queryFn: () => api.questions(situationId, showResolved),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["questions", situationId] });

  const add = useMutation({
    mutationFn: (body: string) => api.addQuestion(situationId, body),
    onSuccess: async () => {
      setDraft("");
      await invalidate();
    },
  });
  const resolve = useMutation({
    mutationFn: ({ id, answer }: { id: string; answer: string | null }) =>
      api.resolveQuestion(situationId, id, answer),
    onSuccess: invalidate,
  });

  const items = questions.data?.items ?? [];

  return (
    <div className="ws">
      {items.length === 0 && !questions.isLoading && (
        <p className="ws-empty">
          {showResolved
            ? "Nothing tracked here yet."
            : "No open questions. What would change your mind about this one?"}
        </p>
      )}

      <ul className="qlist">
        {items.map((q) => (
          <li key={q.id} className={q.resolvedAt ? "q done" : "q"}>
            <div className="qbody">{q.body}</div>
            {q.resolvedAt ? (
              <>
                <div className="qanswer">{q.resolutionMd}</div>
                <div className="qmeta">
                  resolved {q.resolvedAt.slice(0, 10)}
                  {" · "}
                  <button
                    className="linkbtn"
                    onClick={() => resolve.mutate({ id: q.id, answer: null })}
                  >
                    reopen
                  </button>
                </div>
              </>
            ) : (
              <Resolver onResolve={(answer) => resolve.mutate({ id: q.id, answer })} />
            )}
          </li>
        ))}
      </ul>

      <form
        className="ws-add"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) add.mutate(draft);
        }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="What don't you know yet?"
          aria-label="New open question"
        />
        <button className="iconbtn" disabled={!draft.trim() || add.isPending}>
          Track
        </button>
      </form>

      <button className="linkbtn ws-toggle" onClick={() => setShowResolved((v) => !v)}>
        {showResolved ? "Show only open" : "Show resolved too"}
      </button>
    </div>
  );
}

/**
 * Resolving asks for the answer rather than just marking it done: a resolution not
 * recorded loses what made the question worth tracking, and the row is kept for the same
 * reason.
 *
 * @internal
 */
function Resolver({ onResolve }: { onResolve: (answer: string) => void }) {
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState("");

  if (!open) {
    return (
      <div className="qmeta">
        <button className="linkbtn" onClick={() => setOpen(true)}>
          resolve
        </button>
      </div>
    );
  }
  return (
    <form
      className="ws-add"
      onSubmit={(e) => {
        e.preventDefault();
        if (answer.trim()) onResolve(answer);
      }}
    >
      <input
        autoFocus
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="What did it turn out to be?"
        aria-label="Resolution"
      />
      <button className="iconbtn" disabled={!answer.trim()}>
        Save
      </button>
      <button type="button" className="linkbtn" onClick={() => setOpen(false)}>
        cancel
      </button>
    </form>
  );
}

/**
 * The thesis editor: the saved thesis as an editable draft, saved only on an explicit
 * Save, with access to its earlier versions.
 *
 * @internal
 */
function Thesis({ situationId }: { situationId: string }) {
  const qc = useQueryClient();
  const thesis = useQuery({
    queryKey: ["thesis", situationId],
    queryFn: () => api.thesis(situationId),
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  /**
   * The server's copy seeds the editor once, and after that the draft is the owner's:
   * re-syncing on every refetch would discard a sentence in progress.
   */
  useEffect(() => {
    if (draft === null && thesis.data) setDraft(thesis.data.bodyMd);
  }, [thesis.data, draft]);

  const save = useMutation({
    mutationFn: (bodyMd: string) => api.saveThesis(situationId, bodyMd),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["thesis", situationId] });
      await qc.invalidateQueries({ queryKey: ["thesisVersions", situationId] });
    },
  });

  const dirty = draft !== null && thesis.data && draft !== thesis.data.bodyMd;

  return (
    <div className="ws">
      <textarea
        className="thesis"
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="The argument, in your own words. Markdown."
        aria-label="Thesis"
        rows={12}
      />
      <div className="ws-actions">
        <button
          className="iconbtn"
          disabled={!dirty || save.isPending}
          onClick={() => draft !== null && save.mutate(draft)}
        >
          {save.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
        {thesis.data && thesis.data.versionCount > 0 && (
          <button className="linkbtn" onClick={() => setShowHistory((v) => !v)}>
            {thesis.data.versionCount} earlier version
            {thesis.data.versionCount === 1 ? "" : "s"}
          </button>
        )}
        {thesis.data && thesis.data.bodyMd !== "" && (
          <span className="ws-meta">last saved {thesis.data.updatedAt.slice(0, 10)}</span>
        )}
      </div>
      {showHistory && <History situationId={situationId} />}
    </div>
  );
}

/**
 * Every save is snapshotted, so the history is complete rather than curated. `charDelta`
 * makes a long list readable: growth and cuts are each a reason to open that version.
 *
 * @internal
 */
function History({ situationId }: { situationId: string }) {
  const versions = useQuery({
    queryKey: ["thesisVersions", situationId],
    queryFn: () => api.thesisVersions(situationId),
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const body = useQuery({
    queryKey: ["thesisVersion", situationId, openId],
    queryFn: () => api.thesisVersion(situationId, openId!),
    enabled: openId !== null,
  });

  return (
    <div className="vlist">
      {(versions.data?.items ?? []).map((v) => (
        <div key={v.id}>
          <button className="vrow" onClick={() => setOpenId(openId === v.id ? null : v.id)}>
            <span className="vd">{v.savedAt.slice(0, 16).replace("T", " ")}</span>
            <span className={v.charDelta >= 0 ? "vdelta up" : "vdelta down"}>
              {v.charDelta >= 0 ? "+" : "−"}
              {Math.abs(v.charDelta).toLocaleString()}
            </span>
          </button>
          {openId === v.id && <pre className="vbody">{body.data?.bodyMd ?? "…"}</pre>}
        </div>
      ))}
    </div>
  );
}

/**
 * Clipped passages, each with the offsets it came from, so each opens in its filing.
 *
 * There is no "add excerpt" form here: an excerpt is created by selecting text in the
 * document reader, where the offsets are real, and a typed quote would correspond to
 * nothing.
 *
 * @internal
 */
function Excerpts({ situationId }: { situationId: string }) {
  const qc = useQueryClient();
  const excerpts = useQuery({
    queryKey: ["excerpts", situationId],
    queryFn: () => api.excerpts(situationId),
  });
  const remove = useMutation({
    mutationFn: (excerptId: string) => api.deleteExcerpt(situationId, excerptId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["excerpts", situationId] }),
  });

  const items = excerpts.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <>
      <div className="sechd">Excerpts</div>
      <div className="ws">
        {items.map((e) => (
          <figure key={e.id} className="exc">
            <blockquote>{e.quote}</blockquote>
            {e.comment && <figcaption className="exc-note">{e.comment}</figcaption>}
            <div className="exc-meta">
              {e.document ? (
                <Link
                  to={filingHref(e.document.accession, e)}
                >
                  {e.document.formType ?? e.document.accession} @{e.charStart.toLocaleString()}
                </Link>
              ) : (
                <span>source document no longer present</span>
              )}
              {" · "}
              <button className="linkbtn" onClick={() => remove.mutate(e.id)}>
                remove
              </button>
            </div>
          </figure>
        ))}
      </div>
    </>
  );
}
